import type { Primitive } from "type-fest";
import { Result } from "./result.ts";

/**
 * Convert resource key to resource id
 * Eg. `user_1234` to `1234`
 */
export function resourceKeyToResourceId(key: string): string {
  const resourceKey = key.split("_")[1];
  if (resourceKey !== undefined) {
    return resourceKey;
  }
  throw new Error(
    `Invalid resource key, expected a string with '_', but got ${key}`,
  );
}

/**
 * Creates a promise that will resolve in `time` ms.
 * @param time Time in milliseconds
 */
export function sleep(time: number): Promise<void> {
  return new Promise((r) => setTimeout(r, time));
}

/**
 * Ensure a object is an array
 */
export function ensureArray<T>(obj?: T[] | null): T[] {
  return Array.isArray(obj) ? obj : [];
}

export function parseJson<T>(str: string): Result<T, SyntaxError> {
  return Result.try(() => JSON.parse(str));
}

/**
 * Returns random slug
 */
export function makeSlug(length: number): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charsLength = chars.length;

  return new Array(length)
    .fill(null)
    .map(() => chars.charAt(Math.floor(Math.random() * charsLength)))
    .join("");
}

/**
 * Ensure the string has a known value.
 *
 * ```ts
 * const myString: string = 'cat';
 * const animal = stringGuard(myString, 'cat', 'dog');
 * // animal is of type "'cat' | 'dog' | undefined";
 * ```
 */
export function stringGuard<T extends string>(
  str?: string,
  ...knownStrings: readonly T[]
): T | undefined {
  return knownStrings.find((knownString) => knownString === str);
}

/**
 * Returns an array of values of the enumerable properties of an object.
 * Same as `Object.values` but with better types
 */
export function objectValues<Obj extends Record<string, any>>(
  o: Obj,
): Array<Obj[keyof Obj]> {
  return Object.values(o) as Array<Obj[keyof Obj]>;
}

/**
 * Returns an array of keys of the enumerable properties of an object.
 * Same as `Object.keys` but with better types
 */
export const objectKeys = <O extends Record<string, unknown>>(
  o: O,
): Array<keyof O> => Object.keys(o);

/**
 * Transfroms a string to lowerCase. Same as string.toLowerCase but with better types.
 */
export const lowerCase = <Input extends string>(
  input: Input,
): Lowercase<Input> => input.toLowerCase() as Lowercase<Input>;

/**
 * Transfroms a string to upperCase. Same as string.toUpperCase but with better types.
 */
export const upperCase = <Input extends string>(
  input: Input,
): Uppercase<Input> => input.toUpperCase() as Uppercase<Input>;

/**
 * Checks if a value is a primitive type.
 */
export function isPrimitive(value: unknown): value is Primitive {
  return (
    value == null ||
    typeof value === "string" ||
    typeof value === "bigint" ||
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "symbol"
  );
}

/**
 * Escapes special regex characters in a string so it can be safely
 * used inside `new RegExp(...)` as a literal match.
 */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
